import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Image, TextInput, FlatList, RefreshControl, ActivityIndicator, Animated, Share, Alert,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const PROF_KEYS = { PROFILE:'prof_user_profile' };
const SAVED_KEY = 'grad_saved_programs';
const APPS_KEY  = 'grad_applications';

const FIELDS = ['All','Engineering','Technology','Finance','Consulting','Healthcare','Marketing','Law','Data','Other'];
const SORTS  = ['Deadline','Posted','Salary','Company'];

const T = {primary:'#0e4e96',primaryLight:'#1e7be2',primaryBg:'#EFF6FF',success:'#10b981',successBg:'#D1FAE5',warning:'#f59e0b',warningBg:'#FEF3C7',danger:'#ef4444',dangerBg:'#FEE2E2',text:'#1F2937',textSec:'#64748B',textMut:'#94A3B8',border:'#E2E8F0',borderLight:'#F1F5F9',card:'#FFF',bg:'#F8FAFC',white:'#FFF'};

const daysLeft = d => { try { return Math.ceil((new Date(d)-Date.now())/(864e5)); } catch { return null; } };
const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return ''; } };

// ═══════════════════════════════════════════════════════════════════════════════
const MOCK = [
  {id:'gp_001',title:'Engineering Leadership Program',company:'Rolls-Royce',location:'Derby',duration:'2 years',compensation:'£32,000/year',deadline:'2026-07-15',field:'Engineering',isInternational:false,rotations:4,mentorship:true,professionalQualification:true,description:'Develop future technical leaders through rotational placements across aerospace, defense, and power systems.',requirements:['Engineering degree (2:1+)','Leadership potential','Problem-solving skills'],logo:'https://logo.clearbit.com/rolls-royce.com',postedDate:'2026-01-10'},
  {id:'gp_002',title:'Technology Graduate Scheme',company:'BP',location:'London',duration:'3 years',compensation:'£35,000/year',deadline:'2026-08-30',field:'Technology',isInternational:true,rotations:3,mentorship:true,professionalQualification:false,description:'Develop technical and leadership skills working on innovative energy transition solutions.',requirements:['CS/Engineering degree (2:1+)','Analytical skills','Interest in sustainability'],logo:'https://logo.clearbit.com/bp.com',postedDate:'2026-02-01'},
  {id:'gp_003',title:'Graduate Analyst Programme',company:'Goldman Sachs',location:'London',duration:'2 years',compensation:'£55,000/year',deadline:'2026-06-20',field:'Finance',isInternational:true,rotations:2,mentorship:true,professionalQualification:true,description:'Join our world-class investment banking team and develop analytical expertise.',requirements:['Finance/Economics degree','Strong quantitative skills','Teamwork'],logo:'https://logo.clearbit.com/goldmansachs.com',postedDate:'2026-01-15'},
  {id:'gp_004',title:'Management Consulting Graduate',company:'McKinsey',location:'London',duration:'2 years',compensation:'£50,000/year',deadline:'2026-09-01',field:'Consulting',isInternational:true,rotations:3,mentorship:true,professionalQualification:false,description:'Solve complex business challenges for world-leading organizations.',requirements:['Any degree (2:1+)','Exceptional problem-solving','Leadership experience'],logo:'https://logo.clearbit.com/mckinsey.com',postedDate:'2026-02-15'},
  {id:'gp_005',title:'Data Science Graduate Program',company:'Unilever',location:'Remote',duration:'2 years',compensation:'£38,000/year',deadline:'2026-07-01',field:'Data',isInternational:false,rotations:3,mentorship:true,professionalQualification:false,description:'Apply data science and machine learning to drive consumer insights and business strategy.',requirements:['Data Science/Statistics degree','Python/R proficiency','ML fundamentals'],logo:'https://logo.clearbit.com/unilever.com',postedDate:'2026-03-01'},
  {id:'gp_006',title:'Healthcare Leadership Scheme',company:'NHS',location:'Nationwide',duration:'3 years',compensation:'£28,000/year',deadline:'2026-10-15',field:'Healthcare',isInternational:false,rotations:6,mentorship:true,professionalQualification:true,description:'Fast-track into NHS management with rotational placements across hospital trusts.',requirements:['Any degree (2:2+)','Passion for healthcare','Communication skills'],logo:'https://logo.clearbit.com/nhs.uk',postedDate:'2026-04-01'},
];

export default function GraduateProgramsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [programs, setPrograms]     = useState([]);
  const [search, setSearch]         = useState('');
  const [field, setField]           = useState('All');
  const [sortBy, setSortBy]         = useState('Deadline');
  const [savedIds, setSavedIds]     = useState(new Set());
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [userSkills, setUserSkills] = useState([]);

  // ── Load ──
  useEffect(() => { loadData(); }, []);
  useFocusEffect(useCallback(() => { loadSaved(); loadApplied(); }, []));

  const loadData = async () => {
    setIsLoading(true);
    try {
      // API
      const r = await apiFetch('/api/data/internship_programs?is_active=true&sort=created_at&order=desc&limit=30');
      let list = [];
      if (!r.error && Array.isArray(r) && r.length>0) list = r;
      else list = MOCK;
      setPrograms(list);
      // Profile skills
      const profRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE)||await AsyncStorage.getItem('userProfile');
      if (profRaw) {
        const prof = JSON.parse(profRaw);
        setUserSkills((prof.skills||[]).map(s=>(s.name||s.skill_name||'').toLowerCase()).filter(Boolean));
      }
    } catch { setPrograms(MOCK); }
    setIsLoading(false);
    loadSaved(); loadApplied();
  };

  const loadSaved = async () => {
    try { const raw = await AsyncStorage.getItem(SAVED_KEY); if(raw) setSavedIds(new Set(JSON.parse(raw))); } catch {}
  };
  const loadApplied = async () => {
    try { const raw = await AsyncStorage.getItem(APPS_KEY); if(raw) setAppliedIds(new Set(JSON.parse(raw).map(a=>a.programId))); } catch {}
  };

  const toggleSave = async (id) => {
    const next = new Set(savedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSavedIds(next);
    await AsyncStorage.setItem(SAVED_KEY, JSON.stringify([...next]));
  };

  const onRefresh = async () => { setRefreshing(true); await loadData(); setRefreshing(false); };

  // ── Filter & Sort ──
  const filtered = useMemo(() => {
    let list = [...programs];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p=>(p.title||'').toLowerCase().includes(q)||(p.company||'').toLowerCase().includes(q)||(p.location||'').toLowerCase().includes(q)||(p.field||'').toLowerCase().includes(q));
    }
    if (field!=='All') list = list.filter(p=>(p.field||p.category||p.department||'').toLowerCase()===field.toLowerCase());
    list.sort((a,b)=>{
      if(sortBy==='Deadline') return new Date(a.deadline||a.application_deadline||'2099')-new Date(b.deadline||b.application_deadline||'2099');
      if(sortBy==='Posted') return new Date(b.postedDate||b.posted_date||b.created_at||0)-new Date(a.postedDate||a.posted_date||a.created_at||0);
      if(sortBy==='Salary') { const n=s=>{const m=(s||'').match(/\d+/);return m?parseInt(m[0]):0}; return n(b.compensation||b.stipend)-n(a.compensation||a.stipend); }
      if(sortBy==='Company') return (a.company||'').localeCompare(b.company||'');
      return 0;
    });
    return list;
  }, [programs, search, field, sortBy]);

  const stats = useMemo(() => ({
    total: programs.length,
    open: programs.filter(p=>daysLeft(p.deadline||p.application_deadline)>0).length,
    saved: savedIds.size,
    applied: appliedIds.size,
  }), [programs, savedIds, appliedIds]);

  // ── Match Score ──
  const calcMatch = (p) => {
    if (!userSkills.length) return 0;
    const tags = [...(Array.isArray(p.requirements)?p.requirements:[]),...(Array.isArray(p.skills)?p.skills:[])].map(s=>(typeof s==='string'?s:'').toLowerCase());
    if (!tags.length) return 0;
    const matched = tags.filter(t=>userSkills.some(us=>t.includes(us)||us.includes(t))).length;
    return Math.min(100, Math.round((matched/tags.length)*80)+15);
  };

  // ── Render Card ──
  const renderCard = useCallback(({item}) => {
    const dl = daysLeft(item.deadline||item.application_deadline);
    const closed = dl!==null && dl<0;
    const urgent = dl!==null && dl<=14 && dl>0;
    const match = calcMatch(item);
    const saved = savedIds.has(item.id);
    const applied = appliedIds.has(item.id);
    return (
      <TouchableOpacity style={s.card} activeOpacity={0.85}
        onPress={()=>navigation.navigate('GraduateProgramDetails',{programId:item.id,program:item})}>
        {/* Header */}
        <View style={{flexDirection:'row',marginBottom:10}}>
          <View style={s.logo}><Text style={s.logoT}>{(item.company||'?')[0]}</Text></View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={s.cardTitle} numberOfLines={2}>{item.title}</Text>
            <Text style={s.cardCo}>{item.company}</Text>
          </View>
          <TouchableOpacity style={s.saveBtn} onPress={()=>toggleSave(item.id)}>
            <Ionicons name={saved?'bookmark':'bookmark-outline'} size={18} color={saved?T.primary:T.textMut}/>
          </TouchableOpacity>
        </View>
        {/* Meta */}
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:10}}>
          <View style={s.metaChip}><Ionicons name="location-outline" size={12} color={T.textSec}/><Text style={s.metaChipT}>{item.location}</Text></View>
          <View style={s.metaChip}><Ionicons name="time-outline" size={12} color={T.textSec}/><Text style={s.metaChipT}>{item.duration}</Text></View>
          <View style={s.metaChip}><Ionicons name="cash-outline" size={12} color={T.success}/><Text style={[s.metaChipT,{color:T.success,fontWeight:'700'}]}>{item.compensation||item.stipend||'Competitive'}</Text></View>
          {item.isInternational&&<View style={[s.metaChip,{backgroundColor:'#DBEAFE'}]}><Text style={[s.metaChipT,{color:'#2563EB'}]}>🌍 International</Text></View>}
        </View>
        {/* Features */}
        <View style={{flexDirection:'row',gap:8,marginBottom:10}}>
          {item.rotations&&<View style={s.featurePill}><MaterialCommunityIcons name="rotate-3d-variant" size={12} color={T.primary}/><Text style={s.featurePillT}>{item.rotations} Rotations</Text></View>}
          {item.mentorship&&<View style={s.featurePill}><Ionicons name="people-outline" size={12} color={T.primary}/><Text style={s.featurePillT}>Mentorship</Text></View>}
          {item.professionalQualification&&<View style={s.featurePill}><Ionicons name="ribbon-outline" size={12} color={T.primary}/><Text style={s.featurePillT}>Prof. Qual.</Text></View>}
          <View style={[s.featurePill,{backgroundColor:T.primaryBg}]}><Text style={[s.featurePillT,{color:T.primary,fontWeight:'700'}]}>{item.field||item.category||'General'}</Text></View>
        </View>
        {/* Match */}
        {match>0&&<View style={{flexDirection:'row',alignItems:'center',marginBottom:10}}>
          <View style={{flex:1,height:4,backgroundColor:T.borderLight,borderRadius:2,overflow:'hidden'}}><View style={{height:4,borderRadius:2,backgroundColor:match>=70?T.success:match>=40?T.warning:T.danger,width:`${match}%`}}/></View>
          <Text style={{fontSize:11,fontWeight:'700',color:match>=70?T.success:match>=40?T.warning:T.danger,marginLeft:8}}>{match}% match</Text>
        </View>}
        {/* Footer */}
        <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between'}}>
          <View>
            <Text style={{fontSize:11,color:T.textMut}}>Deadline</Text>
            <Text style={{fontSize:13,fontWeight:'700',color:closed?T.danger:urgent?T.warning:T.text}}>
              {closed?'Closed':dl!==null?`${fmtDate(item.deadline||item.application_deadline)} (${dl}d)`:'TBC'}
            </Text>
          </View>
          {applied ? (
            <View style={[s.applyBtn,{backgroundColor:T.successBg}]}><Ionicons name="checkmark-circle" size={14} color={T.success}/><Text style={[s.applyBtnT,{color:T.success}]}>Applied</Text></View>
          ) : closed ? (
            <View style={[s.applyBtn,{backgroundColor:T.borderLight}]}><Text style={[s.applyBtnT,{color:T.textMut}]}>Closed</Text></View>
          ) : (
            <TouchableOpacity style={s.applyBtn} onPress={()=>navigation.navigate('GraduateProgramApplication',{programId:item.id,program:item})}>
              <Text style={s.applyBtnT}>Apply</Text><Ionicons name="chevron-forward" size={14} color={T.white}/>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    );
  }, [savedIds, appliedIds, userSkills]);

  // ── MAIN ──
  if (isLoading) return <SafeAreaView style={{flex:1,backgroundColor:T.bg}}><StatusBar barStyle="light-content"/><View style={{flex:1,justifyContent:'center',alignItems:'center'}}><ActivityIndicator size={36} color={T.primary}/><Text style={{marginTop:12,color:T.textSec}}>Loading programs...</Text></View></SafeAreaView>;

  return (
    <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      {/* Header */}
      <LinearGradient colors={[T.primary,T.primaryLight]} style={s.header}>
        <TouchableOpacity style={s.hdrBtn} onPress={()=>navigation.goBack()}><Ionicons name="arrow-back" size={22} color={T.white}/></TouchableOpacity>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={s.hdrTitle}>Graduate Programs</Text>
          <Text style={s.hdrSub}>{filtered.length} programs available</Text>
        </View>
      </LinearGradient>

      <View style={{flex:1,backgroundColor:T.bg}}>
        {/* Stats */}
        <View style={s.statsRow}>
          {[{l:'Total',v:stats.total,c:T.primary},{l:'Open',v:stats.open,c:T.success},{l:'Saved',v:stats.saved,c:T.warning},{l:'Applied',v:stats.applied,c:'#8B5CF6'}].map(x=>(
            <View key={x.l} style={s.statCard}><Text style={[s.statNum,{color:x.c}]}>{x.v}</Text><Text style={s.statLbl}>{x.l}</Text></View>
          ))}
        </View>

        {/* Search */}
        <View style={s.searchWrap}>
          <View style={s.searchBar}>
            <Ionicons name="search" size={18} color={T.textMut}/>
            <TextInput style={s.searchIn} placeholder="Search by title, company, location..." value={search} onChangeText={setSearch} placeholderTextColor={T.textMut}/>
            {search.length>0&&<TouchableOpacity onPress={()=>setSearch('')}><Ionicons name="close-circle" size={18} color={T.textMut}/></TouchableOpacity>}
          </View>
        </View>

        {/* Filters */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{maxHeight:44}} contentContainerStyle={{paddingHorizontal:16,gap:6,alignItems:'center'}}>
          {FIELDS.map(f=>(
            <TouchableOpacity key={f} style={[s.filterChip,field===f&&s.filterChipActive]} onPress={()=>setField(f)}>
              <Text style={[s.filterChipT,field===f&&{color:T.white}]}>{f}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Sort */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{maxHeight:38,marginTop:6}} contentContainerStyle={{paddingHorizontal:16,gap:6,alignItems:'center'}}>
          {SORTS.map(so=>(
            <TouchableOpacity key={so} style={[s.sortChip,sortBy===so&&s.sortChipActive]} onPress={()=>setSortBy(so)}>
              <Text style={[s.sortChipT,sortBy===so&&{color:T.primary,fontWeight:'700'}]}>{so}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* List */}
        <FlatList
          data={filtered}
          renderItem={renderCard}
          keyExtractor={item=>String(item.id)}
          contentContainerStyle={{padding:16,paddingBottom:30}}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[T.primary]}/>}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={<View style={{alignItems:'center',paddingVertical:40}}><Ionicons name="school-outline" size={48} color={T.textMut}/><Text style={{fontSize:16,fontWeight:'600',color:T.text,marginTop:12}}>No Programs Found</Text><Text style={{fontSize:13,color:T.textSec,marginTop:4}}>Try adjusting your search or filters</Text></View>}
        />
      </View>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  header:{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:{width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:{fontSize:20,fontWeight:'700',color:'#FFF'},
  hdrSub:{fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},
  statsRow:{flexDirection:'row',paddingHorizontal:12,paddingVertical:10,gap:6},
  statCard:{flex:1,backgroundColor:T.card,borderRadius:12,paddingVertical:10,alignItems:'center',shadowColor:'#000',shadowOpacity:0.04,shadowRadius:4,elevation:1},
  statNum:{fontSize:18,fontWeight:'900'},
  statLbl:{fontSize:9,color:T.textSec,fontWeight:'500',marginTop:1},
  searchWrap:{paddingHorizontal:16,paddingVertical:8},
  searchBar:{flexDirection:'row',alignItems:'center',backgroundColor:T.card,borderRadius:12,paddingHorizontal:12,paddingVertical:10,gap:8,borderWidth:1,borderColor:T.border},
  searchIn:{flex:1,fontSize:14,color:T.text,padding:0},
  filterChip:{paddingHorizontal:14,paddingVertical:7,borderRadius:20,backgroundColor:T.card,borderWidth:1,borderColor:T.border},
  filterChipActive:{backgroundColor:T.primary,borderColor:T.primary},
  filterChipT:{fontSize:12,fontWeight:'600',color:T.textSec},
  sortChip:{paddingHorizontal:12,paddingVertical:5,borderRadius:16,backgroundColor:T.card,borderWidth:1,borderColor:T.border},
  sortChipActive:{borderColor:T.primary,backgroundColor:T.primaryBg},
  sortChipT:{fontSize:11,fontWeight:'500',color:T.textSec},
  card:{backgroundColor:T.card,borderRadius:16,padding:16,marginBottom:14,borderWidth:1,borderColor:T.border,shadowColor:'#000',shadowOpacity:0.04,shadowRadius:6,elevation:1},
  logo:{width:44,height:44,borderRadius:12,backgroundColor:T.primaryBg,justifyContent:'center',alignItems:'center'},
  logoT:{fontSize:20,fontWeight:'800',color:T.primary},
  cardTitle:{fontSize:15,fontWeight:'700',color:T.text,lineHeight:20},
  cardCo:{fontSize:13,color:T.primary,fontWeight:'600',marginTop:2},
  saveBtn:{width:36,height:36,borderRadius:10,justifyContent:'center',alignItems:'center',backgroundColor:T.borderLight},
  metaChip:{flexDirection:'row',alignItems:'center',gap:3,backgroundColor:T.borderLight,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  metaChipT:{fontSize:11,fontWeight:'500',color:T.textSec},
  featurePill:{flexDirection:'row',alignItems:'center',gap:3,backgroundColor:T.borderLight,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  featurePillT:{fontSize:10,fontWeight:'600',color:T.textSec},
  applyBtn:{flexDirection:'row',alignItems:'center',gap:4,backgroundColor:T.primary,paddingHorizontal:16,paddingVertical:8,borderRadius:10},
  applyBtnT:{fontSize:13,fontWeight:'700',color:T.white},
});
